import { useState, useEffect } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { TripTimerProps } from '../types/trip';

export default function TripTimer({
    tripStatus,
    scheduleStartedAt,
    estimatedHours,
    isExtended,
    onShowExtendButton
}: TripTimerProps) {
    const [elapsed, setElapsed] = useState({ hours: 0, minutes: 0, seconds: 0 });

    const parseStartTime = (value: string): Date | null => {
        const directDate = new Date(value);
        if (!Number.isNaN(directDate.getTime())) return directDate;

        // Handles API values that may come as "YYYY-MM-DD HH:mm:ss" without timezone marker.
        const normalized = value.includes('T') ? value : value.replace(' ', 'T');
        const normalizedDate = new Date(normalized);
        return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate;
    };

    useEffect(() => {
        if (tripStatus !== 'Running' || !scheduleStartedAt) {
            onShowExtendButton(false);
            return;
        }

        const calculateElapsed = () => {
            const startTime = parseStartTime(scheduleStartedAt);
            if (!startTime) {
                setElapsed({ hours: 0, minutes: 0, seconds: 0 });
                onShowExtendButton(false);
                return;
            }

            const now = new Date();
            const diffInSeconds = Math.max(0, Math.floor((now.getTime() - startTime.getTime()) / 1000));

            const hours = Math.floor(diffInSeconds / 3600);
            const minutes = Math.floor((diffInSeconds % 3600) / 60);
            const seconds = diffInSeconds % 60;

            setElapsed({ hours, minutes, seconds });

            const elapsedMins = Math.floor(diffInSeconds / 60);
            const estimatedMinutes = (Number(estimatedHours) || 0) * 60;

            if (!isExtended) {
                if (elapsedMins >= 45) {
                    onShowExtendButton(true);
                } else {
                    onShowExtendButton(false);
                }
            } else {
                const timeBeforeEnd = estimatedMinutes - elapsedMins;
                if (timeBeforeEnd <= 15 && timeBeforeEnd > 0) {
                    onShowExtendButton(true);
                } else {
                    onShowExtendButton(false);
                }
            }
        };

        calculateElapsed();
        const interval = setInterval(calculateElapsed, 1000);

        return () => clearInterval(interval);
    }, [scheduleStartedAt, tripStatus, isExtended, estimatedHours, onShowExtendButton]);

    const formatTime = (num: number): string => String(num).padStart(2, '0');

    if (tripStatus !== 'Running') return null;

    return (
        <div className="flex items-center gap-2 rounded-xl border border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50 px-3 py-1.5 text-cyan-800 shadow-sm">
            <div className="h-2 w-2 rounded-full bg-cyan-500 animate-pulse"></div>
            <ClockCircleOutlined style={{ fontSize: '16px' }} />
            <span className="font-mono text-base font-bold tracking-wide">
                {formatTime(elapsed.hours)}:{formatTime(elapsed.minutes)}:{formatTime(elapsed.seconds)}
            </span>
        </div>
    );
}